import * as React from 'react';

import logger from 'js/app/loggerSingleton';

import { REPORT_STEPS } from 'bundles/enterprise-admin-analytics/components/DrawerPollingSteps';
import type { ReportDownload } from 'bundles/enterprise-admin-analytics/components/ReportsStatusProvider';

type UseReportsPollingParams = {
  downloads: ReportDownload[];
  updateDownload: (id: string, updates: Partial<ReportDownload>) => void;
  isEnabled: boolean;
};

const DEFAULT_TIMEOUT = 4 * 60 * 60 * 1000; // 4 hours
const DEFAULT_POLLING_FREQUENCY = 1 * 60 * 1000; // 1 minutes

const downloadLink = (fileUrl: string) => {
  const link = document.createElement('a');
  link.href = fileUrl;
  link.rel = 'noopener noreferrer';
  link.style.display = 'none';
  document.body.appendChild(link);
  try {
    link.click();

    // Remove the link after a delay to ensure browser has time to initiate download
    // This is done for handling concurrent downloads correctly
    setTimeout(() => {
      if (link.parentNode) {
        document.body.removeChild(link);
      }
    }, 500);
  } catch (error) {
    logger.error('Error downloading link:', error);
    document.body.removeChild(link);
  }
};

/**
 * Custom hook for polling report generation status using recursive setTimeout
 */
export const useReportsPolling = ({ downloads, updateDownload, isEnabled }: UseReportsPollingParams) => {
  // Track both polling timeout and fallback timeout for each download
  type PollTimers = {
    pollTimeoutId: NodeJS.Timeout;
    fallbackTimeoutId: NodeJS.Timeout;
  };
  const activePollsRef = React.useRef<Map<string, PollTimers>>(new Map());

  const stopPolling = React.useCallback<(downloadId: string) => void>((downloadId: string) => {
    const timers = activePollsRef.current.get(downloadId);

    if (timers) {
      clearTimeout(timers.pollTimeoutId);
      clearTimeout(timers.fallbackTimeoutId);
      activePollsRef.current.delete(downloadId);
    }
  }, []);

  const pollReport = React.useCallback<(download: ReportDownload) => Promise<void>>(
    async (download: ReportDownload) => {
      if (!download.presignedUrl || !isEnabled) {
        stopPolling(download.id);
        return;
      }

      const scheduleNextPoll = () => {
        // Check if polling is still active before scheduling next poll
        if (!activePollsRef.current.has(download.id)) {
          return;
        }
        const nextTimeoutId = setTimeout(() => pollReport(download), DEFAULT_POLLING_FREQUENCY);
        const timers = activePollsRef.current.get(download.id);
        if (timers) {
          activePollsRef.current.set(download.id, {
            ...timers,
            pollTimeoutId: nextTimeoutId,
          });
        }
      };

      try {
        const response = await fetch(download.presignedUrl);

        if (!activePollsRef.current.has(download.id)) {
          return;
        }

        // Case 1: 404 - Report not ready yet, continue polling
        if (response.status === 404) {
          scheduleNextPoll();
          return;
        }

        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const data = await response.json();

        // Check for error states first
        if (data.state === 'UNKNOWN_ERROR') {
          updateDownload(download.id, { status: REPORT_STEPS.ERROR });
          stopPolling(download.id);
        } else if (data.state === 'TIMEOUT') {
          updateDownload(download.id, { status: REPORT_STEPS.TIMEOUT_ERROR });
          stopPolling(download.id);
        } else if (data.state === 'SUCCESS' && data.reportUrl) {
          downloadLink(data.reportUrl);
          updateDownload(download.id, {
            status: REPORT_STEPS.SUCCESS,
            reportUrl: data.reportUrl,
            progress: 100,
          });
          stopPolling(download.id);
        } else if (data.state === 'RUNNING') {
          // Case 3: 200 but reportUrl is null - Report still generating, continue polling
          scheduleNextPoll();
        }
      } catch {
        updateDownload(download.id, { status: REPORT_STEPS.ERROR });
        stopPolling(download.id);
      }
    },
    [isEnabled, updateDownload, stopPolling]
  );

  const startPolling = React.useCallback<(download: ReportDownload) => void>(
    (download: ReportDownload) => {
      if (activePollsRef.current.has(download.id)) return;

      // Set fallback timeout using report-specific timeout
      const fallbackTimeoutId = setTimeout(() => {
        updateDownload(download.id, { status: REPORT_STEPS.TIMEOUT_ERROR });
        stopPolling(download.id);
      }, DEFAULT_TIMEOUT);

      // Start first poll immediately (creates recursive chain)
      const pollTimeoutId = setTimeout(() => {
        pollReport(download);
      }, 0);

      // Track both timers
      activePollsRef.current.set(download.id, {
        pollTimeoutId,
        fallbackTimeoutId,
      });
    },
    [pollReport, updateDownload, stopPolling]
  );

  // Manage polling lifecycle based on downloads and enabled state
  React.useEffect(() => {
    if (!isEnabled) {
      // Stop all existing polling if there are no downloads to poll
      activePollsRef.current.forEach((_, downloadId) => stopPolling(downloadId));
      return;
    }

    // Start/stop polling based on status
    downloads.forEach((download) => {
      const isPolling = activePollsRef.current.has(download.id);
      const shouldPoll = download.status === REPORT_STEPS.LOADING && !!download.presignedUrl;

      if (shouldPoll && !isPolling) {
        startPolling(download);
      } else if (!shouldPoll && isPolling) {
        stopPolling(download.id);
      }
    });
  }, [downloads, isEnabled, startPolling, stopPolling]);

  React.useEffect(() => {
    const polls = activePollsRef.current;

    return () => {
      polls.forEach((timers) => {
        clearTimeout(timers.pollTimeoutId);
        clearTimeout(timers.fallbackTimeoutId);
      });
      polls.clear();
    };
  }, []);

  return {
    activePollingCount: activePollsRef.current.size,
  };
};
